import {
  ValidatorWithMetadata,
  ValidatorWithMetadataError
} from '@sequencehq/utils'
import { PriceEditorPercentageTier, PriceEditorTier } from '../../domain'
import {
  greaterThan,
  greaterThanEqualTo,
  required
} from '@sequencehq/utils/validation'

export const tiersLessThanPrevious: ValidatorWithMetadata<
  { lastUnit: string; id: string }[],
  Record<string, unknown>,
  {
    tierId: string
    property: 'lastUnit'
  }
> = (tiers: { lastUnit: string; id: string }[]) => {
  const { errors } = tiers.reduce(
    (acc, currentTier, idx) => {
      /**
       * The 'lastUnit' of the final tier should always be empty. Therefore, exclude
       * it from the check
       */
      if (
        idx === tiers.length - 1 &&
        !parseFloat(currentTier.lastUnit ?? '0.00')
      ) {
        return {
          ...acc,
          checkedTiers: [...acc.checkedTiers, currentTier]
        }
      }

      if (
        acc.checkedTiers.find(
          tier =>
            parseFloat(tier.lastUnit ?? '0.00') >=
            parseFloat(currentTier.lastUnit ?? '0.00')
        )
      ) {
        return {
          ...acc,
          checkedTiers: [...acc.checkedTiers, currentTier],
          errors: [
            ...acc.errors,
            {
              message: `Tier last unit must be greater than previous tier`,
              metadata: {
                tierId: currentTier.id,
                property: 'lastUnit' as const
              }
            }
          ]
        }
      }

      return {
        ...acc,
        checkedTiers: [...acc.checkedTiers, currentTier]
      }
    },
    {
      checkedTiers: [],
      errors: []
    } as {
      checkedTiers: { lastUnit: string; id: string }[]
      errors: ValidatorWithMetadataError<{
        tierId: string
        property: 'lastUnit'
      }>[]
    }
  )

  return errors
}

export const valueMissing =
  <Tier extends Record<string, unknown> & { id: string }>(tier: Tier) =>
  (
    propName: keyof Tier
  ):
    | ValidatorWithMetadataError<{
        tierId: string
        property: keyof Tier
      }>
    | undefined => {
    if (required(tier[propName]) || greaterThan(0)(tier[propName])) {
      return {
        message: `${propName as string} is required`,
        metadata: {
          tierId: tier.id,
          property: propName
        }
      }
    }

    return
  }

export const percentageTiersMissingValue: ValidatorWithMetadata<
  PriceEditorPercentageTier[],
  Record<string, unknown>,
  {
    tierId: string
    property: keyof PriceEditorPercentageTier
  }
> = (tiers: PriceEditorPercentageTier[]) => {
  return tiers
    .flatMap(currentTier => {
      const unitPercentageMissing = required(currentTier.unitPercentage)
      const lastUnitMissing = valueMissing(currentTier)('lastUnit')

      const errors = [lastUnitMissing, unitPercentageMissing].filter(
        (
          result
        ): result is ValidatorWithMetadataError<{
          tierId: string
          property: keyof PriceEditorPercentageTier
        }> => Boolean(result)
      )

      if (!errors.length) {
        return []
      }

      return errors
    })
    .filter(Boolean)
}

export const percentageFinalTierMissingValue: ValidatorWithMetadata<
  PriceEditorPercentageTier,
  Record<string, unknown>,
  {
    tierId: string
    property: keyof PriceEditorPercentageTier
  }
> = (finalTier: PriceEditorPercentageTier) => {
  const unitPercentageMissing = required(finalTier.unitPercentage)
    ? {
        message: `Unit percentage is required`,
        metadata: {
          tierId: finalTier.id,
          property: 'unitPercentage'
        }
      }
    : null
  const lastUnitPresent = required(finalTier.lastUnit)
    ? null
    : {
        message: `The final tier must be unbounded`,
        metadata: {
          tierId: finalTier.id,
          property: 'lastUnit'
        }
      }

  return [unitPercentageMissing, lastUnitPresent].filter(
    (
      result
    ): result is ValidatorWithMetadataError<{
      tierId: string
      property: keyof PriceEditorPercentageTier
    }> => Boolean(result)
  )
}

export const tiersMissingValue: ValidatorWithMetadata<
  PriceEditorTier[],
  Record<string, unknown>,
  {
    tierId: string
    property: keyof PriceEditorTier
  }
> = (tiers: PriceEditorTier[]) => {
  return tiers
    .flatMap(currentTier => {
      const unitPriceMissing = required(currentTier.unitPrice)
      const lastUnitMissing = valueMissing(currentTier)('lastUnit')

      const errors = [lastUnitMissing, unitPriceMissing].filter(
        (
          result
        ): result is ValidatorWithMetadataError<{
          tierId: string
          property: keyof PriceEditorTier
        }> => Boolean(result)
      )

      if (!errors.length) {
        return []
      }

      return errors
    })
    .filter(Boolean)
}

export const percentageTiersValidator: ValidatorWithMetadata<
  PriceEditorPercentageTier[],
  Record<string, unknown>,
  {
    tierId?: string
    property?: keyof PriceEditorPercentageTier
  }
> = (value: PriceEditorPercentageTier[], additionalData) => {
  if (!Array.isArray(value)) {
    return []
  }

  if (!value.length) {
    return [
      {
        message: 'At least one tier is required',
        metadata: {
          tierId: undefined
        }
      }
    ]
  }

  const tiers = value.slice(0, value.length - 1)
  const finalTier = value[value.length - 1]

  return [
    ...tiersLessThanPrevious(value, additionalData),
    ...percentageTiersMissingValue(tiers, additionalData),
    ...percentageFinalTierMissingValue(finalTier, additionalData)
  ]
}

export const percentageTierLimitsValidator: ValidatorWithMetadata<
  PriceEditorPercentageTier[],
  Record<string, unknown>,
  {
    tierId: string
    property: keyof PriceEditorPercentageTier
  }
> = (value: PriceEditorPercentageTier[]) => {
  return value
    .flatMap(currentTier => {
      const minimumIsNegativeError =
        currentTier.minPrice && greaterThanEqualTo(0)(+currentTier.minPrice)
          ? {
              message: 'Cannot be negative',
              metadata: {
                tierId: currentTier.id,
                property: 'minPrice'
              }
            }
          : undefined
      const maximumIsNegativeError =
        currentTier.maxPrice && greaterThanEqualTo(0)(+currentTier.maxPrice)
          ? {
              message: 'Cannot be negative',
              metadata: {
                tierId: currentTier.id,
                property: 'maxPrice'
              }
            }
          : undefined
      const maximumLessThanMinimumError =
        currentTier.minPrice &&
        currentTier.maxPrice &&
        greaterThan(+currentTier.minPrice)(currentTier.maxPrice)
          ? {
              message: 'Max. must be greater than min.',
              metadata: {
                tierId: currentTier.id,
                property: 'maxPrice'
              }
            }
          : undefined

      return [
        minimumIsNegativeError,
        maximumIsNegativeError,
        maximumLessThanMinimumError
      ]
    })
    .filter(
      (
        error
      ): error is ValidatorWithMetadataError<{
        tierId: string
        property: keyof PriceEditorPercentageTier
      }> => Boolean(error)
    )
}

export const finalTierMissingValue: ValidatorWithMetadata<
  PriceEditorTier,
  Record<string, unknown>,
  {
    tierId: string
    property: keyof PriceEditorTier
  }
> = (finalTier: PriceEditorTier) => {
  const unitPriceMissing = required(finalTier.unitPrice)
    ? {
        message: `Unit percentage is required`,
        metadata: {
          tierId: finalTier.id,
          property: 'unitPercentage'
        }
      }
    : null
  const lastUnitPresent = required(finalTier.lastUnit)
    ? null
    : {
        message: `The final tier must be unbounded`,
        metadata: {
          tierId: finalTier.id,
          property: 'lastUnit'
        }
      }

  return [unitPriceMissing, lastUnitPresent].filter(
    (
      result
    ): result is ValidatorWithMetadataError<{
      tierId: string
      property: keyof PriceEditorTier
    }> => Boolean(result)
  )
}

export const tiersValidator: ValidatorWithMetadata<
  PriceEditorTier[],
  Record<string, unknown>,
  {
    tierId?: string
    property?: keyof PriceEditorTier
  }
> = (value: PriceEditorTier[], additionalData) => {
  if (!Array.isArray(value)) {
    return []
  }

  if (!value.length) {
    return [
      {
        message: 'At least one tier is required',
        metadata: {
          tierId: undefined
        }
      }
    ]
  }

  const tiers = value.slice(0, value.length - 1)
  const finalTier = value[value.length - 1]

  return [
    ...tiersLessThanPrevious(value, additionalData),
    ...tiersMissingValue(tiers, additionalData),
    ...finalTierMissingValue(finalTier, additionalData)
  ]
}
